import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Phone, Mail, ExternalLink, Trophy, Users,
  DollarSign, Leaf, Sun, Battery, Zap, ClipboardList, FileText, Calculator,
  Handshake, Wrench, ShieldCheck, Calendar, Building2, Sparkles, X,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { Dialog, DialogContent } from '../components/ui/dialog';
import SEO from '../components/SEO';
import { companyInfo } from '../data/mock';
import { ClubInterestForm } from '../components/gameon/ClubInterestForm';

const GOV_URL = 'https://www.communitygrants.gov.au/news/game';
const GRANTCONNECT_URL = 'https://www.grants.gov.au/FO/Show?FoUuid=a4696a87-5434-4c86-a17d-c93166f40a97&keyword=fo2025-5550';
const HERO_IMG = '/sports/afl-celebration.jpg';
const COMMUNITY_IMG = '/sports/community-footy.jpg';

const OBJECTIVES = [
  'Support up to 500 community sports clubs to decarbonise their operations and reduce energy bills through energy efficiency, electrification and climate resilience upgrades.',
  'Increase awareness and understanding of energy efficiency, electrification and climate adaptation activities among sports club members and their communities.',
  'Support emissions abatement to contribute to Australia’s target to reduce emissions to 62–70% below 2005 levels by 2035.',
];

const USES = [
  { icon: Sun, t: 'Solar power', d: 'Rooftop solar to cut daytime grid costs across clubrooms, change rooms and lighting.' },
  { icon: Battery, t: 'Battery storage', d: 'Store solar to power evening training, night games and events.' },
  { icon: Zap, t: 'Electrification', d: 'Move off gas — heat pump hot water, efficient heating/cooling and appliances.' },
  { icon: Leaf, t: 'Climate resilience', d: 'Efficiency and adaptation upgrades that protect facilities and lower running costs.' },
];

const SERVICES = [
  { icon: ClipboardList, t: 'Preliminary energy assessments', d: 'A clear picture of your club’s current energy use and biggest savings opportunities.' },
  { icon: FileText, t: 'Solar & battery feasibility reports', d: 'Right-sized solar and storage options tailored to your facility and usage.' },
  { icon: Calculator, t: 'Budget estimates & project scoping', d: 'Transparent scope and costings to plan your upgrade with confidence.' },
  { icon: Handshake, t: 'Grant-ready project development', d: 'Documentation and support to help your application put its best foot forward.' },
  { icon: Wrench, t: 'Solar, battery & electrification install', d: 'End-to-end, accredited installation delivered by our own team.' },
];

const STEPS = [
  { n: '1', t: 'Get in touch', d: 'Tell us about your club and facilities — we’ll book a quick chat.' },
  { n: '2', t: 'Free energy assessment', d: 'We assess your site and identify the highest-impact upgrades.' },
  { n: '3', t: 'Grant-ready proposal', d: 'We prepare feasibility, scope and budget to support your application.' },
  { n: '4', t: 'Apply & install', d: 'Once funding is secured, we deliver the installation end-to-end.' },
];

const FAQS = [
  { q: 'What is the Game On: Teaming up for climate action program?', a: 'It is an Australian Government program, delivered by the Community Grants Hub, that will provide $35.262 million over two funding rounds across three years (2026–27 to 2028–29) to help up to 500 community sports clubs make their facilities more energy efficient and climate resilient. It was announced as part of the Support for the Net Zero Plan.' },
  { q: 'How much funding is available per club?', a: 'The program supports up to 500 clubs. Grants are expected to be between $25,000 and $100,000 per club for eligible upgrades. Final funding amounts and eligibility criteria will be confirmed in the Grant Opportunity Guidelines published on GrantConnect when the round opens.' },
  { q: 'What can the funding be used for?', a: 'Energy efficiency, electrification and climate resilience upgrades — for example solar power, battery storage, moving off gas to efficient electric systems, and other emissions-reducing improvements to club facilities.' },
  { q: 'Who can apply?', a: 'The program is aimed at community sports clubs. Specific eligibility is set out in the official Grant Opportunity Guidelines. We recommend checking the Community Grants Hub and GrantConnect, and we’re happy to help you understand your options.' },
  { q: 'When does it open and how do we apply?', a: 'Final Grant Opportunity Guidelines and supporting documents will be published on GrantConnect when the grant opportunity opens for applications. Get in touch now so your club is assessed and grant-ready when the round opens.' },
  { q: 'How can Supa Energy Hub help?', a: 'We can provide a preliminary energy assessment, solar and battery feasibility report, budget estimates and project scoping, grant-ready project development support, and full solar, battery and electrification installation.' },
];

const GameOnClimateActionPage = () => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupShown, setPopupShown] = useState(false);

  useEffect(() => {
    if (popupShown) return;

    const trigger = () => {
      setPopupOpen(true);
      setPopupShown(true);
    };

    // 1) Time-based: open 10s after load
    const timer = setTimeout(trigger, 10000);

    // 2) Exit-intent: mouse leaves toward the top of the viewport (about to close/switch tab)
    const handleMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [popupShown]);

  return (
  <div className="min-h-screen">
    <SEO
      title="Game On: Teaming Up for Climate Action — Grants for Community Sports Clubs"
      description="The Australian Government's Game On: Teaming up for climate action program helps up to 500 community sports clubs fund energy efficiency, solar, battery and climate resilience upgrades. Supa Energy Hub helps your club get grant-ready."
      jsonLd={[{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      }]}
    />
    <Header />
    <main>
      {/* HERO */}
      <section className="relative flex items-center overflow-hidden min-h-[600px] md:min-h-[640px]">
        <div className="absolute inset-0 w-full h-full" style={{ backgroundImage: `url('${HERO_IMG}')`, backgroundSize: 'cover', backgroundPosition: 'center 30%' }} data-testid="gameon-hero-bg" />
        <div className="absolute inset-0 bg-[#101a14]/80 md:bg-gradient-to-r md:from-[#101a14]/95 md:via-[#101a14]/75 md:to-[#101a14]/25" />
        <div className="absolute -top-10 left-0 w-96 h-96 bg-[#B5D43B]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative z-10 w-full">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-5" data-testid="gameon-eyebrow">
              <Trophy className="w-3.5 h-3.5 text-[#B5D43B]" />
              <span className="text-[11px] font-bold tracking-widest uppercase text-[#B5D43B]">Australian Government Grant · Community Sports Clubs</span>
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.08] tracking-tight mb-4">
              Game On: Teaming Up for <span className="text-[#B5D43B]">Climate Action</span>
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-xl mb-6 leading-relaxed">
              A new Australian Government program helping up to <strong className="text-white">500 community sports clubs</strong> fund
              energy efficiency, solar, battery and climate resilience upgrades — lowering power bills and future-proofing your facilities.
              Supa Energy Hub helps your club get <strong className="text-white">grant-ready</strong>.
            </p>

            {/* Quick stat chips */}
            <div className="flex flex-wrap gap-2 md:gap-3 mb-7" data-testid="gameon-hero-stats">
              {[
                { icon: DollarSign, label: '$25k–$100k per club*' },
                { icon: Users, label: 'Up to 500 clubs' },
                { icon: Leaf, label: '$35.262M program' },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-2 rounded-full border border-white/20">
                  <s.icon className="w-4 h-4 text-[#B5D43B]" />
                  <span className="text-xs md:text-sm font-medium text-white">{s.label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <a href="#club-help">
                <Button className="btn-primary gap-2 text-sm md:text-base px-6 md:px-8 py-3 md:py-4 h-auto w-full sm:w-auto" data-testid="gameon-hero-cta">
                  Get Your Club Grant-Ready <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <a href="#program">
                <Button className="bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-sm text-sm md:text-base px-6 md:px-8 py-3 md:py-4 h-auto w-full sm:w-auto">
                  Learn About the Program
                </Button>
              </a>
            </div>
            <p className="text-[11px] text-white/60 mt-4 max-w-xl">
              *Expected funding range per club, subject to the final Grant Opportunity Guidelines. Program details sourced from the{' '}
              <a href={GOV_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Community Grants Hub</a>.
            </p>
          </div>
        </div>
      </section>

      {/* ABOUT THE PROGRAM */}
      <section id="program" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">About the program</span>
            <h2 className="heading-2 text-[#3D5A47] mb-4">What is “Game On: Teaming Up for Climate Action”?</h2>
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              Announced as part of the Australian Government’s <strong>Support for the Net Zero Plan</strong>, the program will deliver{' '}
              <strong>$35.262 million over two funding rounds</strong>, running over three years from <strong>2026–27 to 2028–29</strong>.
              It’s designed to help up to <strong>500 community sports clubs</strong> upgrade their facilities to be more climate resilient and energy efficient.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed mb-6">
              Delivery will be supported by Program Support Providers engaged by the Department of Climate Change, Energy, the Environment and Water.
            </p>
            <a href={GOV_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3D5A47] hover:text-[#B5D43B]" data-testid="gameon-gov-link">
              Read the official program page (Community Grants Hub) <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6 sm:p-7">
            <h3 className="font-bold text-[#3D5A47] mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#B5D43B]" /> Program objectives</h3>
            <ul className="space-y-4">
              {OBJECTIVES.map((o, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700 leading-relaxed">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FUNDING AT A GLANCE */}
      <section className="py-14 bg-[#3D5A47] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-10">Funding at a glance</h2>
          <div className="grid sm:grid-cols-3 gap-5" data-testid="gameon-funding">
            {[
              { icon: DollarSign, big: '$25k–$100k', label: 'Expected grant per club*' },
              { icon: Users, big: 'Up to 500', label: 'Community sports clubs supported' },
              { icon: Calendar, big: '2026–2029', label: 'Two funding rounds over three years' },
            ].map((c, i) => (
              <div key={i} className="bg-white/5 border border-white/15 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-4"><c.icon className="w-6 h-6 text-[#B5D43B]" /></div>
                <p className="text-3xl font-black text-[#B5D43B]">{c.big}</p>
                <p className="text-sm text-white/75 mt-2">{c.label}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/50 text-center mt-6 max-w-2xl mx-auto">
            *Final funding amounts and eligibility are confirmed in the Grant Opportunity Guidelines published on GrantConnect when the round opens.
          </p>
        </div>
      </section>

      {/* WHAT FUNDING CAN BE USED FOR */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Eligible upgrades</span>
            <h2 className="heading-2 text-[#3D5A47]">What the funding can help pay for</h2>
            <p className="text-gray-600 text-sm mt-3">Energy efficiency, electrification and climate resilience upgrades that cut your club’s bills and emissions.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gameon-uses">
            {USES.map((u, i) => (
              <div key={i} className="bg-[#f8faf5] rounded-2xl border border-gray-100 p-6">
                <div className="w-11 h-11 rounded-xl bg-[#3D5A47] flex items-center justify-center mb-3"><u.icon className="w-5 h-5 text-[#B5D43B]" /></div>
                <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{u.t}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY IMAGE BAND */}
      <section className="relative h-56 md:h-72 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url('${COMMUNITY_IMG}')`, backgroundSize: 'cover', backgroundPosition: 'center 40%' }} />
        <div className="absolute inset-0 bg-[#101a14]/60" />
        <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
          <h2 className="text-xl md:text-3xl font-black text-white max-w-3xl leading-snug">
            Stronger clubs. Lower bills. A more sustainable home ground for your community.
          </h2>
        </div>
      </section>

      {/* HOW SUPA ENERGY HUB HELPS */}
      <section id="club-help" className="py-16 bg-[#f8faf5] border-y border-gray-100 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">How we help your club</span>
            <h2 className="heading-2 text-[#3D5A47]">We help your club get grant-ready</h2>
            <p className="text-gray-600 text-sm mt-3">
              As home energy specialists, we know sports facilities and how to make them more efficient — saving your club money and helping protect the environment. We can support your club with:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gameon-services">
            {SERVICES.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="w-11 h-11 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-3"><s.icon className="w-5 h-5 text-[#3D5A47]" /></div>
                <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{s.t}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / STEPS */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">Simple process</span>
            <h2 className="heading-2 text-[#3D5A47]">How to get started</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gameon-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="relative bg-[#f8faf5] rounded-2xl border border-gray-100 p-6">
                <div className="w-9 h-9 rounded-full bg-[#3D5A47] text-[#B5D43B] font-black flex items-center justify-center mb-3">{s.n}</div>
                <h3 className="font-bold text-sm text-[#3D5A47] mb-1">{s.t}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 bg-[#f0f7e6] border border-[#B5D43B]/40 rounded-2xl p-5 flex items-start gap-3 max-w-3xl mx-auto">
            <Calendar className="w-5 h-5 text-[#3D5A47] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              Final Grant Opportunity Guidelines and supporting documents will be published on{' '}
              <a href={GRANTCONNECT_URL} target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-[#3D5A47]">GrantConnect</a>{' '}
              when the round opens. Start now so your club is assessed and grant-ready the moment applications open.
            </p>
          </div>
        </div>
      </section>

      {/* REGISTER INTEREST FORM */}
      <section id="register" className="py-16 bg-[#eaf2dc] border-y border-[#B5D43B]/30 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 bg-white rounded-full px-3 py-1 mb-4 border border-[#B5D43B]/40">
              <Trophy className="w-3.5 h-3.5 text-[#3D5A47]" />
              <span className="text-[11px] font-bold tracking-widest uppercase text-[#3D5A47]">Register your interest</span>
            </span>
            <h2 className="heading-2 text-[#3D5A47] mb-3">Get your club on the list</h2>
            <p className="text-gray-700 text-sm leading-relaxed mb-5">
              Register your club's interest and our team will be in touch to help you assess your facility, scope the right
              upgrades, and be <strong>grant-ready</strong> the moment the round opens. No obligation.
            </p>
            <ul className="space-y-2.5">
              {['Free preliminary energy assessment', 'Solar & battery feasibility guidance', 'Grant-ready project support'].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <CheckCircle className="w-5 h-5 text-[#3D5A47] flex-shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8">
              <ClubInterestForm testid="club-form-section" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#f8faf5] border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">FAQ</span>
          <h2 className="heading-2 text-[#3D5A47] mb-6">Common questions from clubs</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="gameon-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left text-[#3D5A47] font-semibold text-sm hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600 text-sm">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="py-16 bg-gradient-to-br from-[#3D5A47] to-[#1a2820] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="w-8 h-8 text-[#B5D43B] mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-black mb-3">Is your club considering facility upgrades?</h2>
          <p className="text-white/80 mb-7 max-w-xl mx-auto">
            Talk to our team about your opportunities and the funding available. We’ll help you assess your facility and build a grant-ready plan.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}>
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#1a2820] gap-2 font-bold px-6 py-5 rounded-xl" data-testid="gameon-call">
                <Phone className="w-4 h-4" /> Call {companyInfo.phone}
              </Button>
            </a>
            <a href={`mailto:${companyInfo.email}?subject=Game On Climate Action Grant — Club Enquiry`}>
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 px-6 py-5 rounded-xl" data-testid="gameon-email">
                <Mail className="w-4 h-4" /> Email our team
              </Button>
            </a>
          </div>
          <div className="mt-6">
            <a href="#register" className="inline-flex items-center gap-2 text-sm text-[#B5D43B] hover:text-white font-semibold" data-testid="gameon-estimate-link">
              Or register your club's interest online <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* GOV REFERENCE / DISCLAIMER */}
      <section className="py-8 bg-[#101a14] text-white/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-start gap-3">
          <Building2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/40" />
          <p className="text-xs leading-relaxed">
            Supa Energy Hub is an independent solar, battery and electrification supplier and installer. We are not the Australian Government or a
            program delivery/support provider for “Game On: Teaming up for climate action”. Program details on this page are sourced from the{' '}
            <a href={GOV_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Community Grants Hub</a>{' '}
            (last updated 27 May 2026) and may change. Always confirm current funding amounts, eligibility, dates and how to apply via the official
            program page and the Grant Opportunity Guidelines on{' '}
            <a href={GRANTCONNECT_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">GrantConnect</a>.
          </p>
        </div>
      </section>
    </main>
    <Footer />

    {/* "DON'T MISS OUT" POPUP — auto-opens 10s after load */}
    <Dialog open={popupOpen} onOpenChange={setPopupOpen}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden gap-0 border-0" data-testid="gameon-popup">
        <div className="bg-gradient-to-r from-[#3D5A47] to-[#1a2820] px-6 py-5 text-white relative">
          <button onClick={() => setPopupOpen(false)} className="absolute top-3 right-3 text-white/70 hover:text-white" aria-label="Close" data-testid="gameon-popup-close">
            <X className="w-5 h-5" />
          </button>
          <span className="inline-flex items-center gap-2 bg-[#B5D43B]/20 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-2">
            <Trophy className="w-3.5 h-3.5 text-[#B5D43B]" />
            <span className="text-[10px] font-bold tracking-widest uppercase text-[#B5D43B]">Don't miss out</span>
          </span>
          <h3 className="text-xl font-black leading-tight">Funding for up to 500 clubs — is yours ready?</h3>
          <p className="text-white/80 text-sm mt-1.5">Register your club's interest now and we'll help you get grant-ready before the round opens.</p>
        </div>
        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
          <ClubInterestForm compact testid="club-form-popup" onSuccess={() => setTimeout(() => setPopupOpen(false), 2500)} />
        </div>
      </DialogContent>
    </Dialog>
  </div>
  );
};

export default GameOnClimateActionPage;
